import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, KeyRound, MapPin, Caravan, Clock, AlertTriangle, Wallet, Warehouse,
} from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents, formatDate, formatDimensions, gateHours } from '../../lib/format';
import { SPACE_TYPE_LABELS } from '../../lib/fit';
import GateCode from '../../components/facility/GateCode';

const ACCESS_STYLES = {
  active: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  suspended: 'bg-rose-50 border-rose-200 text-rose-900',
  pending_move_in: 'bg-slate-50 border-slate-200 text-slate-700',
  inactive: 'bg-slate-50 border-slate-200 text-slate-700',
};

export default function LeasesPage() {
  const [leases, setLeases] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.listLeases()
      .then(({ leases: list }) => { if (!cancelled) { setLeases(list); setState('ready'); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setState('error'); } });
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }
  if (state === 'error') {
    return <p className="mx-auto mt-8 max-w-md rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Your spaces</h1>
      <p className="mt-1 text-sm text-slate-600">Gate access, billing, and what&apos;s stored where.</p>

      {leases.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Warehouse className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">You don&apos;t rent any spaces yet</p>
          <Link to="/search" className="btn-primary mx-auto mt-4">Find a space</Link>
        </div>
      )}

      <ul className="mt-6 space-y-4">
        {leases.map((lease) => (
          <li key={lease.public_id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-wrap items-start justify-between gap-4 p-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-extrabold text-slate-900">Unit {lease.unit_number}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                    {SPACE_TYPE_LABELS[lease.space_type] ?? lease.space_type}
                  </span>
                  <StatusBadge status={lease.status} />
                </div>

                <Link to={`/facilities/${lease.facility_slug}`}
                      className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:underline">
                  <MapPin className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                  {lease.facility_name} · {lease.city}, {lease.state_code}
                </Link>

                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1">
                    <Warehouse className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                    {formatDimensions(lease.length_ft, lease.width_ft)}
                  </span>
                  {lease.vehicle_label && (
                    <span className="inline-flex items-center gap-1">
                      <Caravan className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                      {lease.vehicle_label} ({lease.vehicle_length_ft}ft)
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                    {gateHours(lease)}
                  </span>
                </div>

                <p className="mt-2 text-xs text-slate-500">
                  {lease.lease_number} · started {formatDate(lease.starts_on)} ·{' '}
                  {lease.ends_on ? `ends ${formatDate(lease.ends_on)}` : 'month-to-month, no end date'}
                </p>
              </div>

              <div className="shrink-0 text-right">
                <p className="text-xl font-extrabold text-slate-900">{formatCents(lease.monthly_rate_cents)}</p>
                <p className="text-[12px] text-slate-500">/month, billed on the {ordinal(lease.billing_day)}</p>
                {lease.balance_cents > 0 && (
                  <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[12px] font-bold text-rose-800">
                    <Wallet className="h-3 w-3" aria-hidden="true" />
                    {formatCents(lease.balance_cents)} due
                  </p>
                )}
              </div>
            </div>

            {/* Gate access: the thing tenants actually open this page for. */}
            <div className={`flex flex-wrap items-center gap-3 border-t p-4 ${ACCESS_STYLES[lease.access_state.state]}`}>
              <KeyRound className="h-4 w-4 shrink-0" aria-hidden="true" />
              {lease.gate_code ? (
                <div className="min-w-0 flex-1">
                  {/* Fetched rather than read off the lease: the code rotates,
                      so the value that arrived with the page is stale within a
                      minute of the tenant looking at it. */}
                  <GateCode leaseId={lease.public_id} />
                </div>
              ) : (
                <span className="text-xs font-semibold">
                  {lease.access_state.reason ?? 'Gate code unavailable'}
                </span>
              )}
              {lease.access_state.state === 'suspended' && (
                <span className="inline-flex items-center gap-1 text-xs font-bold">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                  Access suspended — {lease.access_state.reason}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  ending: 'bg-amber-100 text-amber-900',
  ended: 'bg-slate-200 text-slate-600',
  cancelled: 'bg-slate-200 text-slate-600',
};

const STATUS_LABELS = {
  active: 'Active', pending: 'Starts soon', past_due: 'Past due',
  ending: 'Ending', ended: 'Ended', cancelled: 'Cancelled',
};

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${STATUS_STYLES[status] ?? STATUS_STYLES.ended}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}
